import {
    ShoppingCart,
    Users,
    Hourglass,
    Map,
    ShieldCheck,
    Megaphone,
    PackageSearch,
    Tag,
    HeartHandshake,
    BatteryCharging,
    ArrowUpRight,
    ArrowRight,
} from "lucide-react";

const FLAGSHIP = {
    icon: ShoppingCart,
    name: "SmartCaddy",
    tag: "Flagship · In test",
    desc:
        "AI-powered smart cart family — Caddy, Caddy Pro, Caddy Vision and Cobot. RFID, vision, online payments and theft protection on one platform.",
    anchor: "https://smartcaddy.store/",
    external: true,
};

const MODULES = [
    {
        icon: Users,
        name: "Visitor Analytics",
        desc: "Footfall counting, journey mapping, conversion funnels, predictive staffing.",
    },
    {
        icon: Hourglass,
        name: "Queue Management",
        desc: "AI-predicted wait times, virtual Q-Skip, staff alerts, live displays.",
    },
    {
        icon: Map,
        name: "Digital Wayfinding",
        desc: "Kiosk towers, ceiling displays, deep-linked mobile app navigation.",
    },
    {
        icon: ShieldCheck,
        name: "Security & LP",
        desc: "Behavioural anomaly detection, RFID gates, smart-cart weight monitoring.",
    },
    {
        icon: Megaphone,
        name: "Retail Media Network",
        desc: "In-cart ads, digital out-of-home and a CPG self-serve portal — new high-margin revenue.",
        highlight: true,
    },
    {
        icon: PackageSearch,
        name: "RFID Inventory",
        desc: "Real-time inventory accuracy, automated replenishment, receiving verification.",
    },
    {
        icon: Tag,
        name: "Electronic Shelf Labels",
        desc: "Cloud-managed price updates, promotional and out-of-stock flags.",
    },
    {
        icon: HeartHandshake,
        name: "Unified Loyalty",
        desc: "Store-wide loyalty account, gamification and predictive offers.",
    },
    {
        icon: BatteryCharging,
        name: "Parallel Charging Rack",
        desc:
            "Idle carts dock on a parallel-charged rack. A single failed slot never takes another cart offline — true redundancy at the dock.",
    },
];

export default function MallSuite() {
    const FlagshipIcon = FLAGSHIP.icon;
    return (
        <section
            id="platform"
            data-testid="retail-suite"
            className="relative bg-rt-bg py-24 lg:py-36"
        >
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                <div className="grid lg:grid-cols-12 gap-8 mb-12">
                    <div className="lg:col-span-7">
                        <div className="rt-eyebrow mb-4">Products / Smart Retail Suite</div>
                        <h2 className="rt-h2 text-4xl md:text-5xl lg:text-6xl text-white">
                            One product live.<br />
                            <span className="text-white/40">Nine modules on the roadmap.</span>
                        </h2>
                    </div>
                    <p className="lg:col-span-5 lg:pt-3 text-rt-text2 text-base lg:text-lg max-w-xl">
                        Modular by design. SmartCaddy is in test today; the remaining nine
                        modules — analytics, retail media, RFID, loyalty and more — share
                        the same data fabric, identity and analytics core.
                    </p>
                </div>

                {/* Flagship — full-width hero card */}
                <a
                    href={FLAGSHIP.anchor}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="product-card-smartcaddy"
                    className="group relative block bg-rt-surface border border-rt-teal/30 hover:border-rt-teal/60 transition-colors mb-px"
                >
                    <div className="grid lg:grid-cols-12 gap-0">
                        <div className="lg:col-span-8 p-8 lg:p-12">
                            <div className="flex items-center gap-3 mb-8">
                                <div className="w-11 h-11 flex items-center justify-center border border-rt-teal/40 bg-rt-teal/10 text-rt-teal">
                                    <FlagshipIcon className="w-5 h-5" />
                                </div>
                                <span className="rt-mono-label !text-[10px] !tracking-[0.18em] text-rt-teal">
                                    {FLAGSHIP.tag}
                                </span>
                            </div>
                            <h3 className="font-heading text-4xl lg:text-5xl text-white tracking-tight mb-4">
                                {FLAGSHIP.name}
                            </h3>
                            <p className="text-rt-text2 text-base lg:text-lg max-w-2xl leading-relaxed">
                                {FLAGSHIP.desc}
                            </p>
                            <div className="mt-8 inline-flex items-center gap-2 bg-rt-teal text-black font-medium px-5 py-3 group-hover:bg-rt-tealDim transition-colors">
                                Visit smartcaddy.store <ArrowUpRight className="w-4 h-4" />
                            </div>
                        </div>
                        <div className="lg:col-span-4 hidden lg:flex items-center justify-center border-l border-rt-teal/20 bg-gradient-to-br from-rt-teal/5 to-transparent p-10">
                            <div className="text-right space-y-5">
                                {["Caddy", "Caddy Pro", "Caddy Vision", "Cobot"].map((n, i) => (
                                    <div key={n} className="flex items-center justify-end gap-3">
                                        <span className="font-heading text-2xl text-white tracking-tight">
                                            {n}
                                        </span>
                                        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-rt-teal w-20 text-left">
                                            {i === 0 ? "RT-CB01" : `RT-SC0${i}`}
                                        </span>
                                    </div>
                                ))}
                                <div className="pt-3 mt-3 border-t border-white/10 inline-flex items-center gap-1.5 text-xs text-white/60 group-hover:text-rt-teal transition-colors">
                                    Four products · one platform <ArrowRight className="w-3.5 h-3.5" />
                                </div>
                            </div>
                        </div>
                    </div>
                </a>

                {/* 9 roadmap modules — perfect 3x3 grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10 border-t-0">
                    {MODULES.map((m, i) => {
                        const Icon = m.icon;
                        return (
                            <div
                                key={i}
                                data-testid={`product-card-${m.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                                className={`group relative p-7 lg:p-9 bg-rt-bg hover:bg-rt-surface transition-colors flex flex-col min-h-[220px] ${
                                    m.highlight ? "ring-1 ring-rt-teal/30" : ""
                                }`}
                            >
                                <div className="flex items-start justify-between mb-8">
                                    <div className="w-11 h-11 flex items-center justify-center border border-white/15 text-white/80 group-hover:text-rt-teal group-hover:border-rt-teal/40 transition-colors">
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <span className="rt-mono-label !text-[10px] !tracking-[0.18em] text-white/40">
                                        Roadmap
                                    </span>
                                </div>
                                <div className="mt-auto">
                                    <h3 className="font-heading tracking-tight text-xl text-white mb-3">
                                        {m.name}
                                    </h3>
                                    <p className="text-rt-text2 leading-relaxed text-sm">
                                        {m.desc}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
